'use client';

import Link from 'next/link';
import { createContext, useCallback, useContext, useEffect, useState, Fragment } from 'react';
import english from '../content/en.json';
import hotelTravel from '../content/hotel-travel.json';
import { routes } from '../lib/site';
import { attachReviewAutoplay } from '../lib/review-autoplay';
import JourneyAutoplay from './JourneyAutoplay';

const Experience = createContext(null);
export const sectors = { retail: "E-ticaret ve perakende", hospitality: hotelTravel.tr.hotel.name, travel: hotelTravel.tr.travel.name, enterprise: 'Kurumsal müşteri hizmetleri' };
export const scenarios = { siparis: { label: 'Sipariş takibi', sector: 'retail' }, stok: { label: 'Nebim’den stok', sector: 'retail' }, transfer: { label: 'Misafir talebi', sector: 'hospitality' }, talep: { label: 'Kurumsal talep', sector: 'enterprise' } };

export function ExperienceProvider({ locale, route, kind, groups = {}, panels = {}, internalPath, languageLinks, children }) {
  const defaults = Object.fromEntries(Object.entries(groups).map(([key, items]) => [key, items[0]]));
  const [selected, setSelected] = useState(defaults);
  const [sector, setSectorState] = useState(sectors[kind] ? kind : 'retail');
  const [scenario, setScenario] = useState(null);
  const [journeys, setJourneys] = useState([]);
  const [locationContext, setLocationContext] = useState({ search: '', hash: '' });
  const t = value => locale === 'en'
    ? (value === hotelTravel.tr.hotel.name ? hotelTravel.en.hotel.name : value === hotelTravel.tr.travel.name ? hotelTravel.en.travel.name : english[value] || value)
    : value;

  useEffect(() => {
    const update = () => setLocationContext({ search: location.search, hash: location.hash });
    update();
    const query = new URLSearchParams(location.search);
    const incoming = query.get('scenario');
    const incomingSector = query.get('sector');
    const valid = scenarios[incoming] && (!sectors[kind] || scenarios[incoming].sector === kind) && (!incomingSector || incomingSector === scenarios[incoming].sector);
    if (valid) {
      setScenario(incoming); setSectorState(scenarios[incoming].sector);
      for (const [id, data] of Object.entries(panels)) if (data.scenario === incoming) setSelected(current => ({ ...current, [data.group]: id }));
    } else if (!sectors[kind] && sectors[incomingSector]) setSectorState(incomingSector);
    window.addEventListener('hashchange', update);
    return () => window.removeEventListener('hashchange', update);
  }, [route, kind, panels]);

  useEffect(() => {
    const cleanups = [...document.querySelectorAll('[data-review]')].map(root => {
      const firstTab = root.querySelector('[role="tab"]');
      const group = firstTab && panels[firstTab.getAttribute('aria-controls')]?.group;
      if (!group) return () => {};
      return attachReviewAutoplay(root, () => setSelected(current => {
        const items = groups[group];
        return { ...current, [group]: items[(items.indexOf(current[group]) + 1) % items.length] };
      }));
    });
    return () => cleanups.forEach(cleanup => cleanup());
  }, [route, groups, panels]);

  useEffect(() => {
    setJourneys([...document.querySelectorAll('.story-window')].flatMap(root => {
      const tab = root.querySelector('.story-tabs [role="tab"]');
      const group = tab && panels[tab.getAttribute('aria-controls')]?.group;
      const mount = root.querySelector('.story-identity');
      return mount && groups[group]?.length > 1 ? [{ root, mount, group }] : [];
    }));
  }, [route, groups, panels]);

  const advanceJourney = useCallback(group => {
    const items = groups[group];
    if (!items?.length) return;
    // Automatic playback changes only the visible step, not focus or lead context.
    setSelected(current => ({ ...current, [group]: items[(items.indexOf(current[group]) + 1) % items.length] }));
  }, [groups]);

  function select(group, target, focus = false) {
    if (!groups[group]?.includes(target)) return;
    setSelected(current => ({ ...current, [group]: target }));
    if (scenarios[panels[target]?.scenario]) {
      const id = panels[target].scenario;
      setScenario(id); setSectorState(scenarios[id].sector);
    }
    if (focus) document.getElementById(panels[target]?.tab)?.focus({ preventScroll: true });
  }
  function setSector(value) {
    setSectorState(value);
    if (scenario && scenarios[scenario].sector !== value) setScenario(null);
  }
  return <Experience.Provider value={{ locale, route, kind, internalPath, languageLinks, t, groups, panels, selected, select, sector, setSector, scenario, locationContext }}>{children}{journeys.map(journey => <JourneyAutoplay key={journey.group} {...journey} advance={advanceJourney} locale={locale}/>)}</Experience.Provider>;
}
export function useExperience() { return useContext(Experience); }

export function ContextLink({ href, children, onClick, ...props }) {
  const { scenario, sector, kind, locationContext } = useExperience();
  let target = href;
  if (href.startsWith('/') && !href.startsWith('//')) {
    const url = new URL(href, 'https://palmate.local');
    const destination = routes.find(r => r.tr === url.pathname || r.en === url.pathname);
    if (props['data-language']) {
      url.search = locationContext.search;
      url.hash = '';
      const defaultSector = sectors[kind] ? kind : 'retail';
      if (sector !== defaultSector) url.searchParams.set('sector', sector);
      else url.searchParams.delete('sector');
      if (scenario) url.searchParams.set('scenario', scenario);
      else url.searchParams.delete('scenario');
    } else if (destination && scenario && !url.searchParams.has('scenario') && (!sectors[destination.kind] || destination.kind === scenarios[scenario].sector)) {
      url.searchParams.set('scenario', scenario); url.searchParams.set('sector', sector);
    }
    // English home has no locale prefix. Make that choice explicit, including
    // home/logo links, so a Turkish preference or an old cached 301 cannot win.
    if (url.pathname === '/') url.searchParams.set('lang', 'en');
    else if (props['data-language']) url.searchParams.delete('lang');
    target = url.pathname + url.search + url.hash;
  }
  if (target.endsWith('#demo')) target = target.slice(0, -5) + '#demo-form';
  // The locale routes have separate root documents; load the selected language directly.
  // Native in-page anchors replace the current hash instead of composing router fragments.
  if (props['data-language'] || target.startsWith('#')) return <a href={target} {...props} onClick={onClick}>{children}</a>;
  return <Link href={target} {...props} onClick={onClick}>{children}</Link>;
}

export function TabButton({ group, target, children, ...props }) {
  const { selected, select, groups, panels } = useExperience();
  const active = selected[group] === target;
  return <button {...props} type="button" role="tab" aria-selected={active} tabIndex={active ? 0 : -1} onClick={() => select(group, target)} onKeyDown={event => {
    const values = groups[group]; const index = values.indexOf(target);
    const steps = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
    const next = event.key === 'Home' ? values[0] : event.key === 'End' ? values.at(-1) : event.key in steps ? values[(index + steps[event.key] + values.length) % values.length] : null;
    if (next) { event.preventDefault(); select(group, next, true); }
  }}>{children}</button>;
}
export function TabPanel({ as: Tag = 'div', group, preserveLayout = false, children, ...props }) {
  const { selected } = useExperience();
  const active = selected[group] === props.id;
  const keepLayout = preserveLayout || props.className?.split(/\s+/).includes('story-panel');
  // Retain carousel cards in the grid so its tallest content sets a stable height.
  // Inactive cards remain unavailable to keyboard and assistive technology.
  return <Tag {...props} role="tabpanel" hidden={keepLayout ? undefined : !active} aria-hidden={keepLayout ? !active : undefined} inert={keepLayout && !active ? true : undefined} data-active={keepLayout ? active : undefined}>
    {preserveLayout ? children : <Fragment key={active ? 'active' : 'inactive'}>{children}</Fragment>}
  </Tag>;
}
export function StepButton({ group, target, step, children, ...props }) {
  const { selected, select, groups } = useExperience();
  return <button {...props} type="button" onClick={() => {
    const items = groups[group];
    const next = target || items[(items.indexOf(selected[group]) + Number(step) + items.length) % items.length];
    select(group, next, true);
  }}>{children}</button>;
}
